import * as Sentry from "@sentry/react";
import { useShallow } from "zustand/shallow";
import dayjs from "dayjs";
import classNames from "classnames";
import { IconButton, ListItemAvatar, ListItemButton, ListItemText } from "@mui/material";
import { mdiAccountGroupOutline, mdiCalendarPlus, mdiClockOutline, mdiCogOutline } from "@mdi/js";
import Icon from "@mdi/react";

import AppBar from "components/AppBar";
import ContrastIcon from "components/ContrastIcon";
import { EmEmoji } from "components/EmojiPicker/EmojiPicker";
import FAB from "components/FAB";
import AnimatedPlaceholder from "components/AnimatedPlaceholder";
import PageLayout from "components/PageLayout";
import useNotifier from "hooks/useNotifier";
import isActiveCountdown from "utils/isActiveCountdown";
import { EventRow } from "api/event";
import useBoundStore from "store";
import lottiePlaceholders from "assets/placeholders";
import compareString from "utils/compareString";
import { hasGroupRole } from "utils/groupRoles";
import VirtualizedList from "components/VirtualizedList";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import styles from "./group.module.scss";

export interface GroupProps {
  backAction?: boolean;
  hideFab?: boolean;

  onCreateEventClick: () => void;
  onEventClick: (event: EventRow) => void;
  onGroupSettingsClick: () => void;
}

function Group(props: GroupProps) {
  const { backAction = true, hideFab = false, onCreateEventClick, onEventClick, onGroupSettingsClick } = props;
  const userAuth = useBoundStore((state) => state.user.auth);
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const selected = useBoundStore((state) => state.selected);
  const { stateEvents, groups } = useBoundStore(
    useShallow((state) => ({
      stateEvents: state.appData.events,
      groups: state.appData.groups,
    }))
  );
  const { showNotification } = useNotifier();
  const events = stateEvents
    .filter((event) => event.group_id === selected.groupId)
    .sort((a, b) => compareString(a.name, b.name));
  const group = groups.find((g) => g.id === selected.groupId);

  /**
   * Create new event
   */
  const handleCreateEvent = () => {
    if (events.length >= 5) {
      showNotification({
        message: "At the moment you can't have more than 5 active events. Close old events to create new ones.",
        severity: "warning",
      });
      Sentry.captureMessage("User tried to create more than 5 events", "info");
    } else {
      selected.set({ eventId: null });
      onCreateEventClick();
    }
  };

  /**
   * Handle event click
   *
   * @param event
   */
  function handleEventClick(event: EventRow): void {
    selected.set({ eventId: event.id });
    onEventClick(event);
  }

  /**
   * Render app bar
   */
  function renderAppBar() {
    return (
      <AppBar
        backAction={backAction}
        title={group?.name ?? "Group"}
        actions={[
          <IconButton aria-label='Group settings' key='action-1' onClick={onGroupSettingsClick} size='large'>
            <ContrastIcon path={mdiCogOutline} size={1} title='Settings' />
          </IconButton>,
        ]}
      />
    );
  }

  /**
   * Generate items which will be used to display de virtualized list
   *
   * @returns
   */
  function generateVirtualizedList(): JSX.Element[] {
    const vrList: JSX.Element[] = [];

    if (events) {
      events.forEach((event) => {
        vrList.push(
          <ListItemButton
            className={classNames({
              [styles.selectedEvent]: event.id === selected.eventId && !isMobileDevice,
            })}
            onClick={() => handleEventClick(event)}
          >
            <ListItemAvatar>
              <EmEmoji id={event.icon.id} size={24} skin={event.icon.skin} />
            </ListItemAvatar>
            <ListItemText
              primary={
                <span className={styles.title}>
                  <Icon
                    path={mdiClockOutline}
                    size={0.65}
                    className={classNames({
                      [styles.successClockIcon]: isActiveCountdown(event.vote_before),
                      [styles.warningClockIcon]: !isActiveCountdown(event.vote_before),
                    })}
                  />
                  <span className={styles.titleText}>{event.name}</span>
                </span>
              }
              secondary={dayjs(event.start_date).format("MMMM DD, HH:mm")}
            />
          </ListItemButton>
        );
      });
    }

    return vrList;
  }

  /** Return null in case group is not defined */
  if (!group) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder
          icon={mdiAccountGroupOutline}
          text='Oops...it seems like this group does not exist anymore!'
        />
      </PageLayout>
    );
  }

  const isAdmin =
    Boolean(group.admins.find((id) => id === userAuth!.id)) || hasGroupRole(group, userAuth!.id, "manage-events");

  return (
    <PageLayout
      appBar={renderAppBar()}
      fab={isAdmin && !hideFab ? <FAB icon={mdiCalendarPlus} title='Create event' onClick={handleCreateEvent} /> : null}
      paddingBottom={false}
    >
      {events.length > 0 ? (
        <VirtualizedList
          estimateSize={72}
          rows={generateVirtualizedList()}
          style={{ paddingTop: isMobileDevice ? 0 : "16px" }}
        />
      ) : (
        <AnimatedPlaceholder
          animationData={lottiePlaceholders.event}
          text={isAdmin ? "Create a new event by pressing the '+' button" : "Currently, there are no active events."}
        />
      )}
    </PageLayout>
  );
}

export default Group;
